import { registerIndicator, type IndicatorDrawParams, type IndicatorSeries } from 'klinecharts'
import { registerOnce } from './registrationUtils'
import { rsiSeries } from './seriesMath'
import { withAlphaOrGray } from './color'

/**
 * Indicator "Background" fills.
 *
 * Two shapes:
 * - **Band fill** — translucent area between an indicator's upper and lower
 *   DATA lines (Bollinger Bands on the price chart, channel indicators).
 * - **Level zone** — translucent area between two fixed horizontal LEVELS in
 *   an oscillator's own pane (RSI 30–70, stochastic/KDJ 20–80), plus dashed
 *   lines at the levels.
 *
 * klinecharts has no built-in area-between-lines primitive and its indicator
 * `draw` hook fully owns the canvas, so the enhanced indicators re-register
 * the built-in templates (same name, same calc — copied from klinecharts
 * 9.8.12, Apache-2.0) with a `draw` that paints the fill and returns `false`
 * to keep the native line figures. Same precedent as `volumeIndicator.ts`.
 *
 * The pure geometry helpers (`bandFillSegments`, `levelZoneRect`) are exported
 * for unit tests and for hosts that register their own custom indicators (the
 * backtest debug-series families reuse the two draw helpers). Alpha
 * composition lives in `./color` (`withAlphaOrGray`).
 */

/* ------------------------------------------------------------------ */
/*  Pure helpers                                                       */
/* ------------------------------------------------------------------ */

export interface BandFillPoint {
  /** Bar index (chart data index). */
  index: number
  upper: number
  lower: number
}

/**
 * Split the visible range into contiguous runs where BOTH band values exist.
 * Warm-up bars (empty calc rows) break the fill into separate polygons
 * instead of bridging the gap with a misleading straight edge.
 */
export function bandFillSegments(
  result: Array<Record<string, unknown> | undefined>,
  visibleRange: { from: number; to: number },
  upperKey: string,
  lowerKey: string,
): BandFillPoint[][] {
  const segments: BandFillPoint[][] = []
  let current: BandFillPoint[] = []
  for (let i = visibleRange.from; i < visibleRange.to; i++) {
    const row = result[i]
    const upper = row?.[upperKey]
    const lower = row?.[lowerKey]
    if (
      typeof upper === 'number' &&
      Number.isFinite(upper) &&
      typeof lower === 'number' &&
      Number.isFinite(lower)
    ) {
      current.push({ index: i, upper, lower })
    } else if (current.length) {
      segments.push(current)
      current = []
    }
  }
  if (current.length) segments.push(current)
  return segments
}

/**
 * Pixel rect of a horizontal level zone, clamped to the pane. Returns null
 * when the zone is entirely scrolled out of the pane's value range.
 */
export function levelZoneRect(
  yFrom: number,
  yTo: number,
  paneHeight: number,
): { y: number; height: number } | null {
  const top = Math.max(0, Math.min(yFrom, yTo))
  const bottom = Math.min(paneHeight, Math.max(yFrom, yTo))
  if (bottom <= top) return null
  return { y: top, height: bottom - top }
}

/* ------------------------------------------------------------------ */
/*  Draw helpers (shared with host-registered indicators)              */
/* ------------------------------------------------------------------ */

/** Default alpha of a band fill relative to its source line color. */
export const BAND_FILL_ALPHA = 0.08
/** Default alpha of an oscillator level zone fill. */
export const ZONE_FILL_ALPHA = 0.07
/** Dashed lines at the zone levels. */
const ZONE_LINE_COLOR = 'rgba(120,123,134,0.45)'

/**
 * Fill the area between two data lines of the indicator being drawn. Call
 * from an indicator's `draw` and keep returning `false` so the native line
 * figures still render on top.
 */
export function drawIndicatorBandFill(
  params: IndicatorDrawParams,
  opts: { upperKey: string; lowerKey: string; color: string },
): void {
  const { ctx, visibleRange, xAxis, yAxis } = params
  const result = (params.indicator as { result?: Array<Record<string, unknown>> }).result
  if (!result?.length) return
  const segments = bandFillSegments(result, visibleRange, opts.upperKey, opts.lowerKey)
  if (!segments.length) return

  ctx.save()
  ctx.fillStyle = opts.color
  for (const segment of segments) {
    ctx.beginPath()
    segment.forEach((p, i) => {
      const x = xAxis.convertToPixel(p.index)
      const y = yAxis.convertToPixel(p.upper)
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    })
    for (let i = segment.length - 1; i >= 0; i--) {
      const p = segment[i]
      ctx.lineTo(xAxis.convertToPixel(p.index), yAxis.convertToPixel(p.lower))
    }
    ctx.closePath()
    ctx.fill()
  }
  ctx.restore()
}

/**
 * Fill the zone between two fixed value levels across the whole pane width
 * and draw dashed lines at the levels (the oscillator background).
 */
export function drawIndicatorLevelZone(
  params: IndicatorDrawParams,
  opts: { from: number; to: number; fillColor: string; lineColor?: string },
): void {
  const { ctx, bounding, yAxis } = params
  const yFrom = yAxis.convertToPixel(opts.from)
  const yTo = yAxis.convertToPixel(opts.to)
  const rect = levelZoneRect(yFrom, yTo, bounding.height)

  ctx.save()
  if (rect) {
    ctx.fillStyle = opts.fillColor
    ctx.fillRect(0, rect.y, bounding.width, rect.height)
  }
  ctx.strokeStyle = opts.lineColor ?? ZONE_LINE_COLOR
  ctx.lineWidth = 1
  ctx.setLineDash([4, 4])
  for (const y of [yFrom, yTo]) {
    if (y < 0 || y > bounding.height) continue
    const snapped = Math.round(y) + 0.5
    ctx.beginPath()
    ctx.moveTo(0, snapped)
    ctx.lineTo(bounding.width, snapped)
    ctx.stroke()
  }
  ctx.restore()
}

/** Effective color of the indicator's Nth line: user style override first,
 *  then the theme default, then chart gray. */
function lineColor(params: IndicatorDrawParams, figIdx: number): string {
  const override = (params.indicator as { styles?: { lines?: Array<{ color?: string }> } }).styles
    ?.lines?.[figIdx]?.color
  const themed = (params.defaultStyles as { lines?: Array<{ color?: string }> }).lines?.[figIdx]
    ?.color
  return override ?? themed ?? '#787b86'
}

/* ------------------------------------------------------------------ */
/*  Enhanced built-in templates                                        */
/* ------------------------------------------------------------------ */

type CalcRow = Record<string, number | undefined>
type Bar = { close: number; high: number; low: number }

/** Standard deviation step of the BOLL calc (klinecharts getBollMd). */
function bollMd(bars: Bar[], ma: number): number {
  let sum = 0
  for (const bar of bars) {
    const d = bar.close - ma
    sum += d * d
  }
  return Math.sqrt(Math.abs(sum) / bars.length)
}

/**
 * Replace the built-in BOLL / RSI / KDJ / WR templates with fill-enhanced
 * versions. BOLL and KDJ keep klinecharts 9.8.12 calc logic with only `draw`
 * added. RSI and WR additionally adopt standard-charting semantics: a single
 * 14-period line by default (klinecharts ships the A-share 3-line trios), and
 * RSI uses Wilder smoothing instead of klinecharts' simple-average (Cutler)
 * variant. Multi-period param arrays still regenerate one figure per period,
 * so persisted layouts with the old 6/12/24 params keep rendering.
 * Module-level idempotent, mirroring `registerVolumeIndicator`.
 */
export const registerIndicatorFillEnhancements = registerOnce(() => {
  // BOLL — translucent fill between the upper and lower bands, tinted from
  // the upper band's line color (figure 0).
  registerIndicator({
    name: 'BOLL',
    shortName: 'BOLL',
    // Type-only import + cast keeps the runtime enum out of the module so
    // tests that mock 'klinecharts' don't need to stub IndicatorSeries.
    series: 'price' as IndicatorSeries,
    calcParams: [20, 2],
    precision: 2,
    shouldOhlc: true,
    figures: [
      { key: 'up', title: 'UP: ', type: 'line' },
      { key: 'mid', title: 'MID: ', type: 'line' },
      { key: 'dn', title: 'DN: ', type: 'line' },
    ],
    calc: (dataList, indicator) => {
      const params = indicator.calcParams as number[]
      const p = params[0] - 1
      let closeSum = 0
      return dataList.map((kLineData, i) => {
        const boll: CalcRow = {}
        closeSum += kLineData.close
        if (i >= p) {
          boll.mid = closeSum / params[0]
          const md = bollMd(dataList.slice(i - p, i + 1), boll.mid)
          boll.up = boll.mid + params[1] * md
          boll.dn = boll.mid - params[1] * md
          closeSum -= dataList[i - p].close
        }
        return boll
      })
    },
    draw: (params: IndicatorDrawParams) => {
      drawIndicatorBandFill(params, {
        upperKey: 'up',
        lowerKey: 'dn',
        color: withAlphaOrGray(lineColor(params, 0), BAND_FILL_ALPHA),
      })
      return false
    },
  })

  // RSI — standard semantics: single Wilder RSI(14) line, 30–70 zone
  // tinted from the first RSI line.
  registerIndicator({
    name: 'RSI',
    shortName: 'RSI',
    calcParams: [14],
    figures: [{ key: 'rsi1', title: 'RSI: ', type: 'line' }],
    regenerateFigures: (params: unknown[]) =>
      params.map((_, index) => ({
        key: `rsi${index + 1}`,
        title: params.length === 1 ? 'RSI: ' : `RSI${index + 1}: `,
        type: 'line',
      })),
    calc: (dataList, indicator) => {
      const params = indicator.calcParams as number[]
      const figures = indicator.figures as Array<{ key: string }>
      const closes = dataList.map((d) => d.close)
      const perParam = params.map((p) => rsiSeries(closes, p))
      return dataList.map((_, i) => {
        const rsi: CalcRow = {}
        params.forEach((_, index) => {
          const v = perParam[index][i]
          if (v != null) rsi[figures[index].key] = v
        })
        return rsi
      })
    },
    draw: (params: IndicatorDrawParams) => {
      drawIndicatorLevelZone(params, {
        from: 30,
        to: 70,
        fillColor: withAlphaOrGray(lineColor(params, 0), ZONE_FILL_ALPHA),
      })
      return false
    },
  })

  // KDJ (stochastic) — 20–80 zone, tinted from the K line.
  registerIndicator({
    name: 'KDJ',
    shortName: 'KDJ',
    calcParams: [9, 3, 3],
    figures: [
      { key: 'k', title: 'K: ', type: 'line' },
      { key: 'd', title: 'D: ', type: 'line' },
      { key: 'j', title: 'J: ', type: 'line' },
    ],
    calc: (dataList, indicator) => {
      const params = indicator.calcParams as number[]
      const result: CalcRow[] = []
      dataList.forEach((kLineData, i) => {
        const kdj: CalcRow = {}
        if (i >= params[0] - 1) {
          const window = dataList.slice(i - (params[0] - 1), i + 1)
          let hn = Number.MIN_SAFE_INTEGER
          let ln = Number.MAX_SAFE_INTEGER
          for (const bar of window) {
            hn = Math.max(bar.high, hn)
            ln = Math.min(bar.low, ln)
          }
          const hnSubLn = hn - ln
          const rsv = ((kLineData.close - ln) / (hnSubLn === 0 ? 1 : hnSubLn)) * 100
          kdj.k = ((params[1] - 1) * (result[i - 1]?.k ?? 50) + rsv) / params[1]
          kdj.d = ((params[2] - 1) * (result[i - 1]?.d ?? 50) + kdj.k) / params[2]
          kdj.j = 3.0 * kdj.k - 2.0 * kdj.d
        }
        result.push(kdj)
      })
      return result
    },
    draw: (params: IndicatorDrawParams) => {
      drawIndicatorLevelZone(params, {
        from: 20,
        to: 80,
        fillColor: withAlphaOrGray(lineColor(params, 0), ZONE_FILL_ALPHA),
      })
      return false
    },
  })

  // WR — standard semantics: single Williams %R(14) line (klinecharts
  // ships a 6/10/14 trio) with the standard −80…−20 zone. Formula matches
  // klinecharts 9.8.12: (close − HH) / (HH − LL) × 100, range −100…0.
  registerIndicator({
    name: 'WR',
    shortName: 'WR',
    calcParams: [14],
    figures: [{ key: 'wr1', title: 'WR: ', type: 'line' }],
    regenerateFigures: (params: unknown[]) =>
      params.map((_, index) => ({
        key: `wr${index + 1}`,
        title: params.length === 1 ? 'WR: ' : `WR${index + 1}: `,
        type: 'line',
      })),
    calc: (dataList, indicator) => {
      const params = indicator.calcParams as number[]
      const figures = indicator.figures as Array<{ key: string }>
      return dataList.map((kLineData, i) => {
        const wr: CalcRow = {}
        params.forEach((p, index) => {
          if (i < p - 1) return
          let hn = -Infinity
          let ln = Infinity
          for (let j = i - (p - 1); j <= i; j++) {
            if (dataList[j].high > hn) hn = dataList[j].high
            if (dataList[j].low < ln) ln = dataList[j].low
          }
          const range = hn - ln
          wr[figures[index].key] = range === 0 ? 0 : ((kLineData.close - hn) / range) * 100
        })
        return wr
      })
    },
    draw: (params: IndicatorDrawParams) => {
      drawIndicatorLevelZone(params, {
        from: -80,
        to: -20,
        fillColor: withAlphaOrGray(lineColor(params, 0), ZONE_FILL_ALPHA),
      })
      return false
    },
  })
})
